// ModalDetailUpahTukang.jsx - Lengkap dengan upload foto
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Chip, Box, Divider,
  Avatar, Paper, Grid, IconButton, CircularProgress,
  Snackbar, Alert
} from '@mui/material';
import {
  Close, Person, AttachMoney, CalendarToday,
  Receipt, Image as ImageIcon, CloudUpload, Delete,
  Visibility
} from '@mui/icons-material';
import { getFotoKwitansiUrl, uploadFotoKwitansi, deleteFotoKwitansi, getUpahTukangById } from '../API/upahTukang';

const formatRp = (n) => {
  if (!n && n !== 0) return 'Rp 0';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export const ModalDetailUpahTukang = ({ open, onClose, data, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refreshData, setRefreshData] = useState(false);

  if (!data) return null;

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validasi ukuran file (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Ukuran file maksimal 5MB', severity: 'error' });
      return;
    }

    // Validasi tipe file
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'File harus berupa gambar', severity: 'error' });
      return;
    }

    setUploading(true);
    try {
      await uploadFotoKwitansi(data.id, file);
      setSnackbar({ open: true, message: 'Foto kwitansi berhasil diupload', severity: 'success' });
      if (onSuccess) onSuccess();
      // Refresh data di modal
      const updated = await getUpahTukangById(data.id);
      // Update data yang ditampilkan
      Object.assign(data, updated.data);
      setRefreshData(!refreshData);
    } catch (error) {
      console.error('Error uploading foto:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Gagal upload foto', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFoto = async (fotoId) => {
    setDeleting(true);
    try {
      await deleteFotoKwitansi(fotoId);
      setSnackbar({ open: true, message: 'Foto kwitansi berhasil dihapus', severity: 'success' });
      if (onSuccess) onSuccess();
      // Refresh data di modal
      const updated = await getUpahTukangById(data.id);
      Object.assign(data, updated.data);
      setRefreshData(!refreshData);
    } catch (error) {
      console.error('Error deleting foto:', error);
      setSnackbar({ open: true, message: 'Gagal hapus foto', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Person />
            <Typography variant="h6">Detail Upah Tukang</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Info Bantuan */}


<Paper sx={{ p: 2, bgcolor: '#f5f7fa', borderRadius: 2 }}>
  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
    Informasi Bantuan
  </Typography>
  
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
    <Typography variant="h6" fontWeight={700} sx={{ color: 'primary.main' }}>
      #{data.bantuanId}
    </Typography>
    <Chip 
      size="small" 
      label={data.Bantuan?.JenisBantuan || '-'} 
      color="primary" 
      variant="outlined"
    />
  </Box>
  
  <Divider sx={{ my: 1.5 }} />
  
  <Stack spacing={1}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>
        Nama KK
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {data.Bantuan?.Questionnaire?.namaLengkapKK || 'Tidak diketahui'}
      </Typography>
    </Box>
    
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>
        Kecamatan
      </Typography>
      <Typography variant="body2">
        {data.Bantuan?.Questionnaire?.kecamatan || 'Tidak diketahui'}
      </Typography>
    </Box>
    
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>
        Kelurahan
      </Typography>
      <Typography variant="body2">
        {data.Bantuan?.Questionnaire?.desaKelurahan || 'Tidak diketahui'}
      </Typography>
    </Box>
    
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>
        Alamat
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {data.Bantuan?.Questionnaire?.alamatRumah || 'Tidak diketahui'}
      </Typography>
    </Box>
  </Stack>
</Paper>

          {/* Detail Upah */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: data.tahap === 'tahap_1' ? '#fff3e0' : '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">Tahap</Typography>
                <Typography variant="h5" fontWeight={700} color={data.tahap === 'tahap_1' ? 'warning.main' : 'success.main'}>
                  {data.tahap === 'tahap_1' ? 'Tahap I' : 'Tahap II'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.tahap === 'tahap_1' ? 'Progress 30-50%' : 'Progress 100%'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="caption" color="text.secondary">Nominal</Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {formatRp(data.totalUpah)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider />

          {/* Detail Lengkap */}
          <Stack spacing={1}>
            <Typography variant="subtitle2">Detail Lengkap</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Tanggal Dibuat</Typography>
                <Typography variant="body1" fontWeight={500}>{formatDate(data.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Tahap</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {data.tahap === 'tahap_1' ? 'Tahap I (30-50%)' : 'Tahap II (100%)'}
                </Typography>
              </Box>
            </Box>
            {data.keterangan && (
              <Box>
                <Typography variant="caption" color="text.secondary">Keterangan</Typography>
                <Typography variant="body2">{data.keterangan}</Typography>
              </Box>
            )}
            {data.Admin && (
              <Box>
                <Typography variant="caption" color="text.secondary">Dibuat Oleh</Typography>
                <Typography variant="body2">{data.Admin.username}</Typography>
              </Box>
            )}
          </Stack>

          {/* Foto Kwitansi */}
          <Divider />
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                Foto Kwitansi
                {data.FotoKwitansi && data.FotoKwitansi.length > 0 && (
                  <Chip size="small" label={`${data.FotoKwitansi.length} foto`} variant="outlined" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Button
                variant="contained"
                size="small"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                disabled={uploading}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  minWidth: 120
                }}
              >
                {uploading ? 'Mengupload...' : 'Upload Foto'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleUpload}
                />
              </Button>
            </Stack>

            {data.FotoKwitansi && data.FotoKwitansi.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {data.FotoKwitansi.map((foto) => (
                  <Box key={foto.id} sx={{ position: 'relative' }}>
                    <img
                      src={getFotoKwitansiUrl(foto.foto_kwitansi)}
                      alt="Kwitansi"
                      style={{
                        width: 150,
                        height: 150,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0'
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24"%3E%3Cpath fill="%23ccc" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/%3E%3C/svg%3E';
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: '#ffebee' },
                        width: 28,
                        height: 28
                      }}
                      onClick={() => handleDeleteFoto(foto.id)}
                      disabled={deleting}
                    >
                      <Delete fontSize="small" sx={{ color: '#d32f2f' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                bgcolor: '#f5f7fa', 
                borderRadius: 2,
                border: '1px dashed #ccc'
              }}>
                <ImageIcon sx={{ fontSize: 48, color: '#ccc' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Belum ada foto kwitansi
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Klik tombol "Upload Foto" untuk menambahkan
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">Tutup</Button>
      </DialogActions>

      {/* Snackbar untuk notifikasi */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};